import { z } from 'zod';
import { FEEDBACK_KEY_PATTERN, STEP_SIGNATURE_PATTERN } from '../events/journey-event.schema.js';

/** The three prompt kinds of functional §6.8: binary appreciation, closed-choice question, short rating. */
export const FEEDBACK_PROMPT_KINDS = ['binary', 'choice', 'rating'] as const;
export type FeedbackPromptKind = (typeof FEEDBACK_PROMPT_KINDS)[number];

/**
 * When the question is asked: after a significant interaction (functional §6.7),
 * or when the visitor clicks the same spot repeatedly, the friction signal of
 * functional §6.3 — the moment to ask what blocks them.
 */
export const FEEDBACK_ASK_MOMENTS = ['interaction', 'repeated_click'] as const;
export type FeedbackAskMoment = (typeof FEEDBACK_ASK_MOMENTS)[number];

export const FEEDBACK_QUESTION_MAX_LENGTH = 200;
export const FEEDBACK_ANSWER_LABEL_MAX_LENGTH = 60;
export const FEEDBACK_MIN_ANSWERS = 2;
export const FEEDBACK_MAX_ANSWERS = 5;
/** Delay between the significant interaction and the question (functional §6.7 "délai avant proposition"). */
export const FEEDBACK_DEFAULT_DELAY_MS = 1_500;

/** One possible answer: a closed key, never free text; `negative` feeds the demand score orientation. */
export const feedbackAnswerSchema = z
  .object({
    key: z.string().regex(FEEDBACK_KEY_PATTERN),
    negative: z.boolean().default(false),
  })
  .strict();
export type FeedbackAnswer = z.infer<typeof feedbackAnswerSchema>;

/** The question and its answer labels in one language (functional §13.2: every active language). */
export const feedbackPromptContentSchema = z
  .object({
    question: z.string().min(1).max(FEEDBACK_QUESTION_MAX_LENGTH),
    answerLabels: z.record(z.string().min(1).max(FEEDBACK_ANSWER_LABEL_MAX_LENGTH)),
  })
  .strict();
export type FeedbackPromptContent = z.infer<typeof feedbackPromptContentSchema>;

/**
 * A feedback question of a project, served to the SDK in the configuration
 * bundle (module M4). Project configuration (technical §21.5): never a guidance,
 * never created by the engine. The announcement, its type and the issuing
 * entity are read by the SDK from the page's declared markers, or from the path.
 */
export const feedbackPromptSchema = z
  .object({
    id: z.string().regex(FEEDBACK_KEY_PATTERN),
    kind: z.enum(FEEDBACK_PROMPT_KINDS),
    /** Steps the question may be asked on, in the signature form the SDK computes. */
    stepSignatures: z.array(z.string().max(128).regex(STEP_SIGNATURE_PATTERN)).min(1).max(50),
    delayMs: z.number().int().min(0).max(30_000).default(FEEDBACK_DEFAULT_DELAY_MS),
    askOn: z.enum(FEEDBACK_ASK_MOMENTS).default('interaction'),
    /** The visitor may add an optional title and text to the answer, masked before sending (ADR 0016). */
    allowsComment: z.boolean().default(false),
    answers: z.array(feedbackAnswerSchema).min(FEEDBACK_MIN_ANSWERS).max(FEEDBACK_MAX_ANSWERS),
    contents: z.record(feedbackPromptContentSchema),
  })
  .strict();
export type FeedbackPrompt = z.infer<typeof feedbackPromptSchema>;

/** The feedback section of the bundle; absent on bundles published before phase 4 (technical §16.9). */
export const bundleFeedbackSchema = z
  .object({
    prompts: z.array(feedbackPromptSchema).max(20),
  })
  .strict();
export type BundleFeedback = z.infer<typeof bundleFeedbackSchema>;

export const NO_FEEDBACK: BundleFeedback = { prompts: [] };
